import { create } from 'zustand';
import axiosClient from '../services/api';

const useAdminStore = create((set, get) => ({
    users: [],
    withdrawals: [],
    platformAnalytics: null,
    growthData: null,
    systemHealth: null,
    pendingCourses: [],
    supportTickets: [],
    pendingInstructors: [],
    categories: [],
    isLoading: false,
    error: null,

    fetchCategories: async (filters = {}) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get('/users/admin/categories');
            set({ categories: response.data.data, isLoading: false });
        } catch (error) {
            set({
                error: error.response?.data?.error || 'Failed to fetch users',
                isLoading: false,
            });
        }
    },

    createCategory: async (formData) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.post('/users/admin/categories', formData);
            await get().fetchCategories();
            return response.data.data;
        } catch (error) {
            set({
                error: error.response?.data?.error || 'Failed to create category',
                isLoading: false,
            });
            return null;
        }
    },


    updateCategory: async (categoryId, formData) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.put(`/users/admin/categories/${categoryId}`, formData);
            await get().fetchCategories();
            return response.data.data;
        } catch (error) {
            set({
                error: error.response?.data?.error || 'Failed to update category',
                isLoading: false,
            });
            return null;
        }
    },


    deleteCategory: async (categoryId) => {
        set({ isLoading: true, error: null });
        try {
            await axiosClient.delete(`/users/admin/categories/${categoryId}`);
            await get().fetchCategories();
            return true;
        } catch (error) {
            set({
                error: error.response?.data?.error || 'Failed to delete category',
                isLoading: false,
            });
            return false;
        }
    },

    fetchUsers: async (filters = {}) => {
        set({ isLoading: true, error: null });
        try {
            const params = {};
            if (filters.startDate) params.startDate = filters.startDate.toISOString();
            if (filters.endDate) params.endDate = filters.endDate.toISOString();

            const response = await axiosClient.get('/users/admin/users', { params });
            set({ users: response.data.users, isLoading: false });
        } catch (error) {
            set({
                error: error.response?.data?.error || 'Failed to fetch users',
                isLoading: false,
            });
        }
    },

    fetchPlatformAnalytics: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get('/analytics/analytics/admin/platform');
            set({ platformAnalytics: response.data.data, isLoading: false });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch platform analytics', isLoading: false });
        }
    },

    fetchGrowthData: async (startDate, endDate) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get('/analytics/analytics/admin/growth', {
                params: {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                }
            });
            set({ growthData: response.data.data, isLoading: false });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch growth data', isLoading: false });
        }
    },

    fetchSystemHealth: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get('/analytics/analytics/admin/health');
            set({ systemHealth: response.data.data, isLoading: false });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch system health', isLoading: false });
        }
    },

    fetchPendingInstructors: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get('/users/admin/users', {
                params: {
                    role: 'instructor',
                    'onboarding.adminVerified': false
                }
            });
            set({ pendingInstructors: response.data.users, isLoading: false });
        } catch (error) {
            set({
                error: error.response?.data?.error || 'Failed to fetch pending instructors',
                isLoading: false,
            });
        }
    },

    fetchPendingCourses: async () => {
        set({ isLoading: true, error: null });
        try {
            const [coursesResponse, instructorsResponse] = await Promise.all([
                axiosClient.get('/courses/admin/courses'),
                axiosClient.get('/users/admin/users', {
                    params: {
                        role: 'instructor',
                        'onboarding.adminVerified': false
                    }
                })
            ]);
            set({
                pendingCourses: coursesResponse.data.data,
                pendingInstructors: instructorsResponse.data.users,
                isLoading: false
            });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch pending courses and instructors', isLoading: false });
        }
    },

    approveCourse: async (courseId) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.put(`/courses/admin/courses/${courseId}/status`, {
                status: 'approved'
            });
            const updatedCourses = get().pendingCourses.filter(course => course._id !== courseId);
            set({ pendingCourses: updatedCourses, isLoading: false });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to approve course';
            set({ error: errorMessage, isLoading: false });
            return null;
        }
    },

    rejectCourse: async (courseId, comments) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.put(`/courses/admin/courses/${courseId}/status`, {
                status: 'rejected',
                rejectionReason: comments
            });
            const updatedCourses = get().pendingCourses.filter(course => course._id !== courseId);
            set({ pendingCourses: updatedCourses, isLoading: false });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to reject course';
            set({ error: errorMessage, isLoading: false });
            return null;
        }
    },


    verifyInstructor: async (instructorId, verified, comments) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.put(`/users/admin/instructors/${instructorId}/verify`, {
                verified,
                comments
            });
            return response.data.instructor;
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to approve course';
            set({ error: errorMessage, isLoading: false });
            return null;
        }
    },

    fetchSupportTickets: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get('/supports/support/tickets');
            set({ supportTickets: response.data.data, isLoading: false });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch support tickets', isLoading: false });
        }
    },

    fetchWithdrawals: async (filters = {}) => {
        set({ isLoading: true, error: null });
        try {
            const params = {};
            if (filters.startDate) params.startDate = filters.startDate.toISOString();
            if (filters.endDate) params.endDate = filters.endDate.toISOString();

            const response = await axiosClient.get('/revenue/withdrawals', { params });
            set({ withdrawals: response.data.data, isLoading: false });
        } catch (error) {
            set({
                error: error.response?.data?.error || 'Failed to fetch withdrawals',
                isLoading: false,
            });
        }
    },
    
    processWithdrawal: async (id, status, rejectionReason) => {
        const res = await axiosClient.put(`/revenue/withdrawals/${id}/process`, {
            status,
            rejectionReason
        });
        return res.data;
    },

    clearError: () => set({ error: null }),
}));

export default useAdminStore;